import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  formatQueryParams,
  parseJsonApiResponse,
  prepareHeaders,
  Response,
  TQueryParams,
} from 'evolve-ui';

export type TBank = {
  id: number;
  ibanCode: string;
  name: string;
  bic?: string;
  active: boolean;
  isForeign: boolean;
};

export type TUpdateBankBody = {
  ibanCode?: string;
  name?: string;
  bic?: string;
  active?: boolean;
  isForeign?: boolean;
};

export type TCreateBankBody = {
  ibanCode: string;
  name: string;
  bic: string;
  active: boolean;
  isForeign: boolean;
};

export const banksApi = createApi({
  reducerPath: 'banks',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.GATSBY_BANKS_API_URL,
    mode: 'cors',
    prepareHeaders,
  }),
  tagTypes: ['Bank'],
  endpoints: (builder) => ({
    getBanks: builder.query<Response<TBank>, TQueryParams>({
      providesTags: ['Bank'],
      query: formatQueryParams,
      transformResponse: parseJsonApiResponse<TBank>,
    }),
    getBank: builder.query<Response<TBank>, { id: number }>({
      providesTags: ['Bank'],
      query: ({ id }) => `/${id}`,
      transformResponse: parseJsonApiResponse<TBank>,
    }),
    updateBank: builder.mutation<
      Response<TBank>,
      { id: number; bank: TUpdateBankBody }
    >({
      invalidatesTags: ['Bank'],
      query: ({ id, bank }) => ({
        url: `/${id}`,
        method: 'PATCH',
        body: bank,
      }),
      transformResponse: parseJsonApiResponse<TBank>,
    }),
    createBank: builder.mutation<Response<TBank>, { bank: TCreateBankBody }>({
      invalidatesTags: ['Bank'],
      query: ({ bank }) => ({
        url: '',
        method: 'POST',
        body: bank,
      }),
      transformResponse: parseJsonApiResponse<TBank>,
    }),
  }),
});

export const {
  useGetBanksQuery,
  useGetBankQuery,
  useUpdateBankMutation,
  useCreateBankMutation,
} = banksApi;
